import { useState, useEffect, useCallback } from "react";

interface UseWebBuilderPreloaderOptions {
  minLoadingTime?: number;
  dependencies?: unknown[];
  loadingSteps?: string[];
}

export function useWebBuilderPreloader({
  minLoadingTime = 2000,
  dependencies = [],
  loadingSteps = [
    "Initializing editor...",
    "Loading components...",
    "Setting up canvas...",
    "Preparing workspace...",
    "Almost ready...",
  ],
}: UseWebBuilderPreloaderOptions = {}) {
  const [isPreloaderVisible, setIsPreloaderVisible] = useState(true);
  const [loadingStartTime] = useState(Date.now());
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);

  // Check if all dependencies are loaded
  const isDataLoaded = dependencies.every((dep) => {
    if (Array.isArray(dep)) return dep.length >= 0; // For arrays, check if they exist
    if (typeof dep === "boolean") return dep === true; // For loading states, check if true (loading complete)
    return dep != null; // For other values, check if they exist
  });

  const hidePreloader = useCallback(() => {
    const elapsedTime = Date.now() - loadingStartTime;
    const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

    setTimeout(() => {
      setIsPreloaderVisible(false);
    }, remainingTime);
  }, [loadingStartTime, minLoadingTime]);

  // Progress simulation
  useEffect(() => {
    if (!isPreloaderVisible) return;

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev + Math.random() * 3;
        // Cap progress at 85% until data is loaded
        const maxProgress = isDataLoaded ? 100 : 85;
        return Math.min(newProgress, maxProgress);
      });
    }, 100);

    const stepInterval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % loadingSteps.length);
    }, 1500);

    return () => {
      clearInterval(progressInterval);
      clearInterval(stepInterval);
    };
  }, [isPreloaderVisible, isDataLoaded, loadingSteps.length]);

  // Hide preloader when data is loaded and minimum time has passed
  useEffect(() => {
    if (isDataLoaded && isPreloaderVisible) {
      setProgress(100);
      setTimeout(hidePreloader, 500);
    }
  }, [isDataLoaded, isPreloaderVisible, hidePreloader]);

  return {
    isPreloaderVisible,
    progress,
    currentStep: loadingSteps[currentStep],
    hidePreloader: () => {
      setIsPreloaderVisible(false);
    },
  };
}
